// Chat-bot trigger actions. They call the extension server's chat
// endpoints through `botService` (the renderer-side REST client) so the
// TriggerEngine can talk back to Twitch / YouTube / Kick chat.
//
// All four actions are payload-aware: when the rule was fired by an
// `event_chat` dispatch, `context.eventPayload` carries the full
// `ChatMessage` and is used for ${user}/${platform}/${text}
// interpolation (send) and for messageId / userId targeting
// (delete / timeout / ban). Loop safety: the extension's BotEngine
// skips broadcaster-authored messages, so bot-sent replies can never
// re-trigger chat rules into an infinite loop.

import botService from "../../services/bot/botService";

import type { ActionExecutor } from "../TriggerEngine.interfaces";

interface ChatLikePayload {
  id?: unknown;
  platform?: unknown;
  authorId?: unknown;
  authorName?: unknown;
  text?: unknown;
}

const str = (value: unknown): string =>
  typeof value === "string" ? value : "";

const chatPayload = (payload: unknown): ChatLikePayload =>
  payload && typeof payload === "object" ? (payload as ChatLikePayload) : {};

/** `__all__` / empty → undefined (extension broadcasts to all platforms). */
const resolvePlatform = (configured?: string): string | undefined =>
  configured && configured !== "__all__" ? configured : undefined;

/**
 * Replace ${user} / ${platform} / ${text} with values from the
 * triggering chat message (when available). Unknown tokens are left
 * untouched so users get visible feedback on typos.
 */
const interpolate = (template: string, payload: ChatLikePayload): string =>
  template
    .replaceAll("${user}", str(payload.authorName))
    .replaceAll("${platform}", str(payload.platform))
    .replaceAll("${text}", str(payload.text));

export const sendChatMessage: ActionExecutor = async (
  _radio,
  config,
  context,
) => {
  const template = config?.text;

  if (!template) return;

  const payload = chatPayload(context.eventPayload);

  const text = interpolate(template, payload).trim();

  if (!text) return;

  await botService.sendChatMessage({
    platform: resolvePlatform(config.platform),
    text,
  });
};

export const deleteMessage: ActionExecutor = async (
  _radio,
  _config,
  context,
) => {
  const payload = chatPayload(context.eventPayload);

  const messageId = str(payload.id);

  const platform = str(payload.platform);

  // Only meaningful when fired by a chat message — without a target
  // message there is nothing to delete.
  if (!messageId || !platform) return;

  await botService.moderateChat({
    platform,
    action: "delete",
    messageId,
    userId: str(payload.authorId) || undefined,
    userName: str(payload.authorName) || undefined,
  });
};

export const timeoutUser: ActionExecutor = async (_radio, config, context) => {
  const payload = chatPayload(context.eventPayload);

  const platform = str(payload.platform);

  const userId = str(payload.authorId);

  const userName = str(payload.authorName);

  if (!platform || (!userId && !userName)) return;

  const parsed = Number(config?.durationSeconds);

  const durationSeconds =
    Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 600;

  await botService.moderateChat({
    platform,
    action: "timeout",
    messageId: str(payload.id) || undefined,
    userId: userId || undefined,
    userName: userName || undefined,
    durationSeconds,
  });
};

export const banUser: ActionExecutor = async (_radio, _config, context) => {
  const payload = chatPayload(context.eventPayload);

  const platform = str(payload.platform);

  const userId = str(payload.authorId);

  const userName = str(payload.authorName);

  if (!platform || (!userId && !userName)) return;

  await botService.moderateChat({
    platform,
    action: "ban",
    messageId: str(payload.id) || undefined,
    userId: userId || undefined,
    userName: userName || undefined,
  });
};
